// @flow
import * as React from 'react';
import Modal from 'react-modal';

import AdminNewAutoMessageFormSidebar from './AdminNewAutoMessageFormSidebar';
import Button from 'components/Button';
import ConfirmDialog from 'components/ConfirmDialog';
import LinkConstants from 'constants/LinkConstants';
import WizardLayout from 'components/WizardLayout';

import type { WizardStepNode } from 'components/WizardSteps';
import type { AutoMessageTab, AutoMessageTabKey } from './';
import type { AdminNewAutoMessageOrgs_organizations } from './queries/__generated__/AdminNewAutoMessageOrgs';
import type { GetAutoMessageWithoutSends_autoMessaging_autoMessages_autoMessages } from 'admin/components/AdminAutoMessages/queries/__generated__/GetAutoMessageWithoutSends';
import type { AdminAutoMessageSftpCredentials_autoMessaging_sftpCredentials } from 'admin/components/AdminAutoMessageSftpCredentials/queries/__generated__/AdminAutoMessageSftpCredentials';

type Props = {
  onBack: () => void,
  onFieldChange: () => void,
  onResetFieldChange: () => void,
  onSave: (fields: *, onSuccess: Function) => void,
  orgs: AdminNewAutoMessageOrgs_organizations[],
  selectedOrg: ?AdminNewAutoMessageOrgs_organizations,
  autoMessageFields: GetAutoMessageWithoutSends_autoMessaging_autoMessages_autoMessages,
  possibleSftpCredentials: AdminAutoMessageSftpCredentials_autoMessaging_sftpCredentials[],
  tabs: AutoMessageTab[],
  startingTab: ?*,
  isEditingExistingAutoMessage: boolean,
  hasCustomizedMessaging: boolean,
};

type State = {
  currentTab: AutoMessageTab,
  confirmationDialog: ?{
    futureTabKey: AutoMessageTabKey,
    show: boolean,
  },
  fieldsHaveBeenChanged: boolean,
};

export default class AdminNewAutoMessageForms extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const startingTab =
      props.startingTab && props.tabs.find(tab => tab.tabKey === props.startingTab);

    this.state = {
      currentTab: startingTab || props.tabs[0],
      confirmationDialog: undefined,
      fieldsHaveBeenChanged: false,
    };
  }

  advanceTab = () => {
    const { nextStep } = this.state.currentTab;
    const newTab = this.props.tabs.find(tab => tab.tabKey === nextStep);
    this.resetFieldChangeState();
    this.setState({ currentTab: newTab });
  };

  backtrackTab = () => {
    const { prevStep } = this.state.currentTab;
    const newTab = this.props.tabs.find(tab => tab.tabKey === prevStep);
    this.resetFieldChangeState();
    this.setState({ currentTab: newTab });
  };

  jumpToTab = (tabKey: AutoMessageTabKey) => {
    const currentTab = this.props.tabs.find(tab => tab.tabKey === tabKey);
    this.resetFieldChangeState();
    this.setState({ currentTab });
  };

  hideConfirmationDialog = () => {
    this.setState({
      confirmationDialog: undefined,
    });
  };

  showConfirmationDialog = (futureTabKey: AutoMessageTabKey) => {
    this.setState({
      confirmationDialog: {
        show: true,
        futureTabKey,
      },
    });
  };

  handleFieldChange = () => {
    this.setState({ fieldsHaveBeenChanged: true });
    this.props.onFieldChange();
  };

  resetFieldChangeState = () => {
    this.setState({ fieldsHaveBeenChanged: false });
    this.props.onResetFieldChange();
  };

  renderSidebar = (selectedOrg: *) => {
    const { autoMessageFields } = this.props;
    const { currentTab, fieldsHaveBeenChanged } = this.state;

    const nodes: WizardStepNode[] = this.props.tabs.map(tab => ({
      name: tab.name,
      complete: tab.isComplete,
      active: currentTab.tabKey === tab.tabKey,
      nodeKey: tab.tabKey,
    }));

    if (!selectedOrg) {
      console.error('AdminAutoMessageForms - tried to render without selecting an org');
      return null;
    }

    return (
      <AdminNewAutoMessageFormSidebar
        messageName={autoMessageFields.messageName}
        status={autoMessageFields.status}
        selectedOrgName={selectedOrg.name}
        nodes={nodes}
        onClickNode={tabKey => {
          if (fieldsHaveBeenChanged) {
            this.showConfirmationDialog(tabKey);
            return;
          }
          this.jumpToTab(tabKey);
        }}
      />
    );
  };

  render() {
    const {
      orgs,
      autoMessageFields,
      selectedOrg,
      tabs,
      possibleSftpCredentials,
      isEditingExistingAutoMessage,
      hasCustomizedMessaging,
    } = this.props;
    const { uuid: messageUuid, sftpCredentialsUsername } = autoMessageFields;
    const { confirmationDialog, currentTab } = this.state;
    const CurrentTab = currentTab.component;
    const formatCSVTab = tabs.find(tab => tab.tabKey === 'format-csv');
    const syncDataTab = tabs.find(tab => tab.tabKey === 'sync-data');
    const composeTab = tabs.find(tab => tab.tabKey === 'compose');

    const sftpCredentials = possibleSftpCredentials.find(
      creds => creds.credentials.username === sftpCredentialsUsername
    );

    return (
      <React.Fragment>
        {confirmationDialog && (
          <Modal isOpen={confirmationDialog.show}>
            <ConfirmDialog
              actionButtonText="Discard"
              cancelButtonText="Cancel"
              onCancel={this.hideConfirmationDialog}
              onConfirm={() => this.jumpToTab(confirmationDialog.futureTabKey)}
              onRequestHide={this.hideConfirmationDialog}
              title="Your changes will be discarded. Are you sure you want to continue?"
            />
          </Modal>
        )}
        <WizardLayout
          wrapInView={false}
          headerIcon="pencil-stroke-square"
          headerIconColor="shamrock"
          title={isEditingExistingAutoMessage ? 'Edit auto message' : 'New auto message'}
          onBack={this.props.onBack}
          nav={
            <Button
              href={LinkConstants.AUTO_MESSAGING_HELP}
              target="_blank"
              color="polar-bear"
              level="secondary"
              size="xsmall">
              Help
            </Button>
          }
          sidebar={this.renderSidebar(selectedOrg)}>
          <CurrentTab
            {...autoMessageFields}
            messageUuid={messageUuid}
            selectedOrg={selectedOrg}
            sftpCredentials={sftpCredentials}
            possibleSftpCredentials={possibleSftpCredentials}
            potentialOrgs={orgs}
            onSave={this.props.onSave}
            onAdvance={this.advanceTab}
            onBack={this.backtrackTab}
            onJump={this.jumpToTab}
            onClose={this.props.onBack}
            onChangeField={this.handleFieldChange}
            onResetFieldChange={this.resetFieldChangeState}
            formatCSVComplete={formatCSVTab && formatCSVTab.isComplete}
            syncDataComplete={syncDataTab && syncDataTab.isComplete}
            composeComplete={composeTab && composeTab.isComplete}
            hasCustomizedMessaging={hasCustomizedMessaging}
          />
        </WizardLayout>
      </React.Fragment>
    );
  }
}
